/**
 * ServiceWizardVisualEditor — edit a wizard definition with UI controls
 * (steps, questions, relations) instead of raw JSON.
 *
 * Fully controlled: receives `definition` and calls `onChange(newDefinition)`.
 * The raw-JSON mode stays available in the parent as a fallback/power tool.
 *
 * Cada PASO se muestra como una página propia (editor paginado) para no
 * tener que recorrer una lista enorme, y la última página agrupa las
 * relaciones comunes. Todos los campos llevan un tooltip de ayuda.
 */
import React, { useState, useEffect } from 'react';
import {
  PlusIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon,
  ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline';

const QUESTION_TYPES = [
  { value: 'text', label: 'Texto', hint: 'Respuesta escrita a mano. Puede ofrecer sugerencias de elementos existentes (opción «Sugerir elementos existentes»).' },
  { value: 'number', label: 'Número', hint: 'Valor numérico (puerto, cantidad, MB…).' },
  { value: 'choice', label: 'Elección (una)', hint: 'El usuario elige UNA opción de una lista.' },
  { value: 'multichoice', label: 'Elección (varias)', hint: 'El usuario puede marcar VARIAS opciones de la lista.' },
  { value: 'yesno', label: 'Sí / No', hint: 'Interruptor de dos valores (sí o no).' },
  { value: 'pick_ci', label: 'Elegir elemento', hint: 'Selector de un elemento que YA existe en la CMDB.' },
  { value: 'multi_pick_ci', label: 'Elegir varios', hint: 'Selector de varios elementos existentes en la CMDB.' },
  { value: 'pick_or_create_ci', label: 'Elegir o crear', hint: 'Elegir un elemento existente o crear uno nuevo al vuelo.' },
  { value: 'multi_pick_or_create_ci', label: 'Varios: elegir o crear', hint: 'Varios elementos, eligiendo o creando cada uno.' },
  { value: 'installed_software', label: 'Software instalado', hint: 'Elige un producto del inventario del host (o lo escribe a mano).' },
  { value: 'repeat', label: 'Repetidor (filas)', hint: 'Una tabla de filas repetibles (p. ej. los trabajos de backup).' },
];

const RELATION_TYPES = [
  'depends_on', 'uses', 'contains', 'runs_on', 'protected_by', 'authenticated_by',
  'authenticates_on', 'has_backup', 'communicates_with', 'provides', 'member_of',
  'uses_lun', 'exported_by', 'uses_storage', 'stores_on', 'replica_to',
  'ha_peer_with', 'monitors', 'manages', 'syncs_with', 'integrates',
  'has_policy', 'applies_to',
];

const CI_TYPES = [
  'service', 'rack_element', 'virt_vm', 'firewall', 'router', 'workstation',
  'printer', 'wifi_controller', 'wifi_access_point', 'wifi_ssid', 'virt_hypervisor',
  'virt_cluster', 'virt_datastore', 'storage_disk', 'storage_lun', 'server_volume',
  'ipam_subnet', 'ipam_ip', 'isp_circuit', 'rack', 'database_instance',
];

const arr = (v) => (Array.isArray(v) ? v : []);
const csv = (v) => arr(v).join(', ');
const parseCsv = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
const optItems = (opts) => arr(opts).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));

// ── small UI atoms ─────────────────────────────────────────────────────────────
// Tooltip de ayuda: burbuja que aparece al pasar el ratón por el «?».
function HelpTip({ text }) {
  if (!text) return null;
  return (
    <span className="vw-tip" tabIndex={0} role="note" aria-label={text} onClick={(e) => e.preventDefault()}>
      ?
      <span className="vw-tip-bubble">{text}</span>
    </span>
  );
}

function Field({ label, tip, children, grow, className = '' }) {
  return (
    <label className={`vw-field ${grow ? 'vw-grow' : ''} ${className}`}>
      <span className="vw-field-label">{label}{tip ? <HelpTip text={tip} /> : null}</span>
      {children}
    </label>
  );
}

// ── option rows (value / label) ────────────────────────────────────────────────
function OptionsEditor({ options, onChange, label = 'Opciones' }) {
  const items = optItems(options);
  const upd = (i, patch) => onChange(items.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  return (
    <div className="vw-sub">
      <div className="vw-sub-title">
        {label}
        <HelpTip text="Cada opción tiene un valor interno (lo que se guarda) y una etiqueta (lo que se ve). Si no pones etiqueta, se usa el valor." />
      </div>
      {items.map((o, i) => (
        <div className="vw-mini vw-mini-opt" key={i}>
          <input className="sd-builder-input" value={o.value || ''} placeholder="valor interno"
            onChange={(e) => upd(i, { value: e.target.value })} />
          <input className="sd-builder-input" value={o.label || ''} placeholder="etiqueta (opcional)"
            onChange={(e) => upd(i, { label: e.target.value })} />
          <button type="button" className="vw-x" title="Quitar opción"
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
            <TrashIcon width={14} height={14} />
          </button>
        </div>
      ))}
      <button type="button" className="sd-builder-add" onClick={() => onChange([...items, { value: '', label: '' }])}>
        <PlusIcon width={13} height={13} /> Añadir opción
      </button>
    </div>
  );
}

// ── create-field rows (id / label / type / required) ───────────────────────────
function CreateFieldsEditor({ fields, onChange }) {
  const items = arr(fields);
  const upd = (i, patch) => onChange(items.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  return (
    <div className="vw-sub">
      <div className="vw-sub-title">
        Campos al crear el elemento
        <HelpTip text="Formulario que se muestra cuando el usuario decide crear el elemento nuevo: id interno, etiqueta visible, tipo de dato y si es obligatorio." />
      </div>
      {items.map((f, i) => (
        <div className="vw-mini vw-mini-create" key={i}>
          <input className="sd-builder-input" value={f.id || ''} placeholder="id"
            onChange={(e) => upd(i, { id: e.target.value })} />
          <input className="sd-builder-input" value={f.label || ''} placeholder="etiqueta"
            onChange={(e) => upd(i, { label: e.target.value })} />
          <select className="sd-builder-input" value={f.type || 'text'} onChange={(e) => upd(i, { type: e.target.value })}>
            <option value="text">Texto</option>
            <option value="number">Número</option>
            <option value="choice">Elección</option>
          </select>
          <label className="vw-req"><input type="checkbox" checked={!!f.required}
            onChange={(e) => upd(i, { required: e.target.checked })} /> obl.</label>
          <button type="button" className="vw-x" title="Quitar campo"
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
            <TrashIcon width={14} height={14} />
          </button>
        </div>
      ))}
      <button type="button" className="sd-builder-add" onClick={() => onChange([...items, { id: '', label: '', type: 'text' }])}>
        <PlusIcon width={13} height={13} /> Añadir campo
      </button>
    </div>
  );
}

// ── repeat-field rows (id / label) ─────────────────────────────────────────────
function RepeatFieldsEditor({ fields, onChange }) {
  const items = arr(fields);
  const upd = (i, patch) => onChange(items.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  return (
    <div className="vw-sub">
      <div className="vw-sub-title">
        Columnas de la fila
        <HelpTip text="Cada columna del repetidor: id interno y etiqueta visible en la tabla." />
      </div>
      {items.map((f, i) => (
        <div className="vw-mini vw-mini-repeat" key={i}>
          <input className="sd-builder-input" value={f.id || ''} placeholder="id"
            onChange={(e) => upd(i, { id: e.target.value })} />
          <input className="sd-builder-input" value={f.label || ''} placeholder="etiqueta"
            onChange={(e) => upd(i, { label: e.target.value })} />
          <button type="button" className="vw-x" title="Quitar columna"
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
            <TrashIcon width={14} height={14} />
          </button>
        </div>
      ))}
      <button type="button" className="sd-builder-add" onClick={() => onChange([...items, { id: '', label: '' }])}>
        <PlusIcon width={13} height={13} /> Añadir columna
      </button>
    </div>
  );
}

// ── show_if condition editor ───────────────────────────────────────────────────
function ShowIfEditor({ cond, questionIds, onChange }) {
  const op = cond ? ('eq' in cond ? 'eq' : 'ne' in cond ? 'ne' : 'in' in cond ? 'in' : 'eq') : 'eq';
  const val = cond ? (cond.eq ?? cond.ne ?? (cond.in ? csv(cond.in) : '')) : '';
  const set = (patch) => {
    const q = (patch.q ?? cond?.q ?? '');
    const o = (patch.op ?? op);
    const v = (patch.val ?? val);
    if (!q && !v) { onChange(null); return; }
    const condObj = { q, [o]: o === 'in' ? parseCsv(v) : v };
    onChange(condObj);
  };
  return (
    <div className="vw-sub">
      <div className="vw-sub-title">
        Mostrar solo si…
        <HelpTip text="Condición para que esta pregunta aparezca. Si la dejas vacía, se muestra siempre. Dos preguntas del mismo paso pueden encadenar condiciones sobre las anteriores." />
      </div>
      <div className="vw-mini vw-mini-showif">
        <select className="sd-builder-input" value={cond?.q || ''} onChange={(e) => set({ q: e.target.value })}>
          <option value="">— siempre —</option>
          {questionIds.map((id) => <option key={id} value={id}>{id}</option>)}
        </select>
        <select className="sd-builder-input" value={op} onChange={(e) => set({ op: e.target.value })}>
          <option value="eq">es igual a</option>
          <option value="ne">es distinto de</option>
          <option value="in">está entre</option>
        </select>
        <input className="sd-builder-input" value={val} placeholder={op === 'in' ? 'a, b, c' : 'valor'}
          onChange={(e) => set({ val: e.target.value })} />
      </div>
    </div>
  );
}

// ── one question ───────────────────────────────────────────────────────────────
function QuestionEditor({ q, index, questionIds, onChange, onRemove, onMove }) {
  const upd = (patch) => onChange({ ...q, ...patch });
  const t = q.type || 'text';
  const typeHint = (QUESTION_TYPES.find((x) => x.value === t) || {}).hint;
  const isPick = t === 'pick_ci' || t === 'multi_pick_ci' || t === 'multi_pick_or_create_ci';
  const canCreate = t === 'pick_or_create_ci' || t === 'multi_pick_or_create_ci';
  return (
    <div className="vw-q">
      <div className="vw-q-head">
        <span className="vw-q-num">Pregunta {index}</span>
        <div className="vw-q-head-actions">
          <label className="vw-req">
            <input type="checkbox" checked={!!q.required} onChange={(e) => upd({ required: e.target.checked })} />
            Obligatoria <HelpTip text="No se puede pasar al siguiente paso sin responderla." />
          </label>
          <button type="button" className="vw-x" onClick={() => onMove(-1)} title="Subir pregunta"><ArrowUpIcon width={13} height={13} /></button>
          <button type="button" className="vw-x" onClick={() => onMove(1)} title="Bajar pregunta"><ArrowDownIcon width={13} height={13} /></button>
          <button type="button" className="vw-x" onClick={onRemove} title="Quitar pregunta"><TrashIcon width={14} height={14} /></button>
        </div>
      </div>

      <div className="vw-q-grid vw-q-grid-3">
        <Field label="Identificador (id)" tip="Nombre interno de la pregunta, sin espacios ni acentos (se limpia solo). Se usa en las relaciones y en las condiciones «mostrar si».">
          <input className="sd-builder-input" value={q.id || ''} placeholder="id"
            onChange={(e) => upd({ id: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })} />
        </Field>
        <Field label="Tipo" tip={typeHint}>
          <select className="sd-builder-input" value={t} onChange={(e) => upd({ type: e.target.value })}>
            {QUESTION_TYPES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </select>
        </Field>
        <Field label="Pregunta" grow tip="Texto que verá el usuario en el asistente.">
          <input className="sd-builder-input" value={q.label || ''} placeholder="pregunta"
            onChange={(e) => upd({ label: e.target.value })} />
        </Field>
      </div>

      <div className="vw-q-grid vw-q-grid-2">
        <Field label="Ayuda" tip="Explicación corta que aparece justo bajo la pregunta para orientar al usuario.">
          <input className="sd-builder-input" value={q.help || ''} onChange={(e) => upd({ help: e.target.value })} />
        </Field>
        <Field label="Guardar en config (writes)" tip="Ruta del config_json donde se guarda la respuesta (p. ej. vpn.client_pool). Si la dejas vacía, la respuesta no se guarda en la ficha del servicio.">
          <input className="sd-builder-input" value={q.writes || ''} placeholder="p. ej. vpn.client_pool"
            onChange={(e) => upd({ writes: e.target.value })} />
        </Field>
      </div>

      {(t === 'choice' || t === 'multichoice') && (
        <OptionsEditor options={q.options} onChange={(o) => upd({ options: o })} />
      )}

      {isPick && (
        <Field label="Tipos permitidos" tip="Tipos de elemento de la CMDB que se podrán elegir. Marca los chips o escríbelos separados por comas.">
          <input className="sd-builder-input" value={csv(q.ci_types)}
            onChange={(e) => upd({ ci_types: parseCsv(e.target.value) })} />
          <div className="vw-chips">
            {CI_TYPES.map((c) => (
              <button type="button" key={c} className={`vw-chip ${arr(q.ci_types).includes(c) ? 'is-on' : ''}`}
                onClick={() => {
                  const cur = arr(q.ci_types);
                  upd({ ci_types: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] });
                }}>{c}</button>
            ))}
          </div>
        </Field>
      )}

      {canCreate && (
        <>
          <Field label="Tipo de CI a crear/elegir" tip="Tipo de elemento que se creará si el usuario no elige uno existente.">
            <input className="sd-builder-input" value={q.ci_type || ''}
              onChange={(e) => upd({ ci_type: e.target.value })} />
          </Field>
          <CreateFieldsEditor fields={q.create_fields} onChange={(f) => upd({ create_fields: f })} />
          <Field label="Valores por defecto al crear (JSON, opcional)" tip='Valores fijos que se aplican al crear el elemento, en JSON. Ej.: {"element_type":"storage"}.'>
            <input className="sd-builder-input" value={q.create_defaults ? JSON.stringify(q.create_defaults) : ''}
              placeholder='{"element_type":"storage"}'
              onChange={(e) => {
                const v = e.target.value.trim();
                if (!v) { upd({ create_defaults: undefined }); return; }
                try { upd({ create_defaults: JSON.parse(v) }); } catch { /* ignore until valid */ }
              }} />
          </Field>
        </>
      )}

      {t === 'text' && (
        <Field label="Sugerir elementos existentes" tip="Debajo del campo de texto se ofrecerá elegir un elemento ya existente de estos tipos, para rellenar el texto con él.">
          <input className="sd-builder-input" value={csv(q.suggest_ci_types)}
            onChange={(e) => upd({ suggest_ci_types: parseCsv(e.target.value) })} />
        </Field>
      )}

      {t === 'repeat' && (
        <>
          <Field label="Etiqueta de fila / botón" tip="Nombre corto de cada fila y del botón para añadir una (p. ej. «sede»).">
            <input className="sd-builder-input" value={q.item_label || ''} placeholder="p. ej. sede"
              onChange={(e) => upd({ item_label: e.target.value })} />
          </Field>
          <RepeatFieldsEditor fields={q.fields} onChange={(f) => upd({ fields: f })} />
        </>
      )}

      <Field label="Filtro por columna (JSON, opcional)" tip='Acota los elementos que se listan, filtrando por columnas del modelo. Ej.: {"rack_element":{"element_type":["server"]}}.'>
        <input className="sd-builder-input" value={q.filter ? JSON.stringify(q.filter) : ''}
          placeholder='{"rack_element":{"element_type":["server"]}}'
          onChange={(e) => {
            const v = e.target.value.trim();
            if (!v) { upd({ filter: undefined }); return; }
            try { upd({ filter: JSON.parse(v) }); } catch { /* ignore until valid */ }
          }} />
      </Field>

      <ShowIfEditor cond={q.show_if} questionIds={questionIds} onChange={(c) => upd({ show_if: c || undefined })} />
    </div>
  );
}

// ── one step = one page ────────────────────────────────────────────────────────
function StepPage({ step, questionIds, onChange, onRemove, onMove }) {
  const questions = arr(step.questions);
  const setQ = (qs) => onChange({ ...step, questions: qs });
  return (
    <div className="vw-page-body">
      <div className="vw-step-head">
        <Field label="Identificador del paso" tip="Nombre interno del paso, sin espacios ni acentos (se limpia solo). Solo sirve como referencia.">
          <input className="sd-builder-input" value={step.id || ''} placeholder="id del paso"
            onChange={(e) => onChange({ ...step, id: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })} />
        </Field>
        <Field label="Título del paso" grow tip="Título que verá el usuario en la cabecera de este paso (p. ej. «Trabajos del servidor de backup»).">
          <input className="sd-builder-input" value={step.title || ''} placeholder="título del paso"
            onChange={(e) => onChange({ ...step, title: e.target.value })} />
        </Field>
        <div className="vw-step-head-actions">
          <button type="button" className="vw-x" onClick={() => onMove(-1)} title="Subir paso"><ArrowUpIcon width={13} height={13} /></button>
          <button type="button" className="vw-x" onClick={() => onMove(1)} title="Bajar paso"><ArrowDownIcon width={13} height={13} /></button>
          <button type="button" className="vw-x" onClick={onRemove} title="Eliminar paso"><TrashIcon width={14} height={14} /></button>
        </div>
      </div>

      <Field label="Ayuda del paso" tip="Explicación que se muestra bajo el título del paso para orientar al usuario.">
        <input className="sd-builder-input" value={step.help || ''}
          onChange={(e) => onChange({ ...step, help: e.target.value })} />
      </Field>

      <div className="vw-qlist">
        {questions.length === 0 && (
          <div className="vw-empty-sm">Este paso no tiene preguntas todavía. Añade la primera abajo.</div>
        )}
        {questions.map((q, qi) => (
          <QuestionEditor
            key={qi}
            q={q}
            index={qi + 1}
            questionIds={questionIds}
            onChange={(nq) => setQ(questions.map((x, idx) => (idx === qi ? nq : x)))}
            onRemove={() => setQ(questions.filter((_, idx) => idx !== qi))}
            onMove={(d) => {
              const j = qi + d; if (j < 0 || j >= questions.length) return;
              const c = [...questions]; [c[qi], c[j]] = [c[j], c[qi]]; setQ(c);
            }}
          />
        ))}
      </div>
      <button type="button" className="sd-builder-add"
        onClick={() => setQ([...questions, { id: `q${questions.length + 1}`, type: 'text', label: '' }])}>
        <PlusIcon width={13} height={13} /> Añadir pregunta
      </button>
    </div>
  );
}

// ── relations page (shared by the whole wizard) ────────────────────────────────
function RelationsPage({ relations, questionIds, hostFrom, onChange, onChangeHost }) {
  return (
    <div className="vw-page-body">
      <div className="vw-rel-intro">
        Cada relación se lee <strong>origen → verbo → destino</strong>. Tanto el origen como el destino
        pueden ser el propio servicio (<code>service</code>) o el identificador de una pregunta (<code>id</code>).
        Los campos de origen y destino sugieren los ids disponibles al escribir.
      </div>
      <div className="vw-mini vw-mini-rel vw-rel-head">
        <span className="vw-field-label">Tipo (verbo) <HelpTip text="Qué relación hay entre el origen y el destino (ejecuta en, usa, protege con…)." /></span>
        <span className="vw-field-label">Origen <HelpTip text="Quién empieza la relación: «service» (el propio servicio) o el id de una pregunta." /></span>
        <span className="vw-field-label">Destino <HelpTip text="Quién recibe la relación: «service» (el propio servicio) o el id de una pregunta." /></span>
        <span />
      </div>
      {relations.map((r, i) => (
        <div className="vw-mini vw-mini-rel" key={i}>
          <select className="sd-builder-input" value={r.type || ''} title="Tipo de relación"
            onChange={(e) => onChange(relations.map((x, idx) => (idx === i ? { ...x, type: e.target.value } : x)))}>
            {RELATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input className="sd-builder-input" value={r.from || ''} placeholder="desde (qid o service)" list="vw-qids"
            onChange={(e) => onChange(relations.map((x, idx) => (idx === i ? { ...x, from: e.target.value } : x)))} />
          <input className="sd-builder-input" value={r.to || ''} placeholder="hacia (qid o service)" list="vw-qids"
            onChange={(e) => onChange(relations.map((x, idx) => (idx === i ? { ...x, to: e.target.value } : x)))} />
          <button type="button" className="vw-x" title="Quitar relación"
            onClick={() => onChange(relations.filter((_, idx) => idx !== i))}>
            <TrashIcon width={14} height={14} />
          </button>
        </div>
      ))}
      <datalist id="vw-qids">
        <option value="service" />
        {questionIds.map((id) => <option key={id} value={id} />)}
      </datalist>
      <button type="button" className="sd-builder-add"
        onClick={() => onChange([...relations, { type: 'uses', from: '', to: 'service' }])}>
        <PlusIcon width={13} height={13} /> Añadir relación
      </button>

      <Field label="Alojado en (opcional)" tip="Id de la pregunta cuyo elemento se usará como host del servicio (rellena «Alojamiento» en la ficha). Si hay varias candidatas, gana la primera con respuesta.">
        <input className="sd-builder-input" value={hostFrom || ''} list="vw-qids"
          placeholder="qid del CI que será el host"
          onChange={(e) => onChangeHost(e.target.value || undefined)} />
      </Field>
    </div>
  );
}

export default function ServiceWizardVisualEditor({ definition, onChange }) {
  const def = definition || { steps: [], relations: [] };
  const steps = arr(def.steps);
  const relations = arr(def.relations);
  const allQuestionIds = steps.flatMap((s) => arr(s.questions).map((q) => q.id)).filter(Boolean);

  // `active` = índice del paso visible, o 'relations' para la última página.
  const [active, setActive] = useState(0);

  // Si se borra el paso activo (o cambia el número de pasos), recolocar la página.
  useEffect(() => {
    if (active === 'relations') return;
    if (steps.length === 0) { if (active !== 0) setActive(0); return; }
    if (active > steps.length - 1) setActive(steps.length - 1);
  }, [steps.length, active]);

  const setDef = (patch) => onChange({ ...def, ...patch });
  const setSteps = (s) => setDef({ steps: s });
  const setRelations = (r) => setDef({ relations: r });

  const updStep = (i, patch) => setSteps(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const moveStep = (i, d) => {
    const j = i + d; if (j < 0 || j >= steps.length) return;
    const c = [...steps]; [c[i], c[j]] = [c[j], c[i]]; setSteps(c); setActive(j);
  };
  const addStep = () => {
    const n = steps.length;
    setSteps([...steps, { id: `paso${n + 1}`, title: `Paso ${n + 1}`, questions: [] }]);
    setActive(n);
  };
  const removeStep = (i) => setSteps(steps.filter((_, idx) => idx !== i));

  const isRelations = active === 'relations';
  const cur = typeof active === 'number' ? active : -1;

  const goPrev = () => {
    if (isRelations) { setActive(steps.length - 1); return; }
    if (cur > 0) setActive(cur - 1);
  };
  const goNext = () => {
    if (isRelations) return;
    if (cur < steps.length - 1) setActive(cur + 1);
    else setActive('relations');
  };

  return (
    <div className="vw-root">
      <div className="vw-pager">
        {steps.map((s, i) => (
          <button type="button" key={i}
            className={`vw-pager-tab ${!isRelations && cur === i ? 'is-on' : ''}`}
            onClick={() => setActive(i)} title={s.title || s.id || `Paso ${i + 1}`}>
            <span className="vw-pager-num">{i + 1}</span>
            <span className="vw-pager-name">{s.title || s.id || `Paso ${i + 1}`}</span>
          </button>
        ))}
        <button type="button" className="vw-pager-tab vw-pager-add" onClick={addStep} title="Añadir un paso nuevo">
          <PlusIcon width={14} height={14} /> Paso
        </button>
        <button type="button" className={`vw-pager-tab ${isRelations ? 'is-on' : ''}`}
          onClick={() => setActive('relations')} title="Relaciones y alojamiento del servicio">
          <span className="vw-pager-num">↔</span>
          <span className="vw-pager-name">Relaciones</span>
        </button>
      </div>

      <div className="vw-page">
        {isRelations ? (
          <RelationsPage
            relations={relations}
            questionIds={allQuestionIds}
            hostFrom={def.host_from}
            onChange={setRelations}
            onChangeHost={(v) => setDef({ host_from: v })}
          />
        ) : steps[cur] ? (
          <StepPage
            key={cur}
            step={steps[cur]}
            questionIds={allQuestionIds}
            onChange={(s) => updStep(cur, s)}
            onRemove={() => removeStep(cur)}
            onMove={(d) => moveStep(cur, d)}
          />
        ) : (
          <div className="vw-empty">
            Este asistente todavía no tiene pasos. Pulsa <strong>«+ Paso»</strong> para crear el primero.
          </div>
        )}
      </div>

      <div className="vw-pager-nav">
        <button type="button" className="sd-cancel-btn" onClick={goPrev}
          disabled={steps.length === 0 || (!isRelations && cur <= 0)}>
          <ChevronLeftIcon width={14} height={14} /> Anterior
        </button>
        <span className="vw-pager-pos">
          {isRelations ? 'Relaciones del asistente'
            : steps.length ? `Paso ${cur + 1} de ${steps.length}` : 'Sin pasos'}
        </span>
        <button type="button" className="sd-save-btn" onClick={goNext}
          disabled={steps.length === 0 || isRelations}>
          {!isRelations && cur === steps.length - 1 ? 'Relaciones' : 'Siguiente'}
          <ChevronRightIcon width={14} height={14} />
        </button>
      </div>
    </div>
  );
}
